/* Poppy (26 Sep 2026): make the variant picker feel instant.
   1. Prefetch the section Dawn will request as soon as a shopper hovers, touches or focuses a swatch/pill,
      and hand that response to Dawn's own fetch when they click (abort-safe).
   2. On click, cross-fade the main photo to the chosen style straight away while the store catches up.
   3. Mark the page ready after the first load so the pop-in animation never replays on re-render. */
(() => {
  if (window.poppySwatches) return;
  window.poppySwatches = true;

  const root = document.documentElement;
  const markReady = () => setTimeout(() => root.classList.add('poppy-sw-ready'), 700);
  if (document.readyState === 'complete') markReady();
  else window.addEventListener('load', markReady, { once: true });
  setTimeout(() => root.classList.add('poppy-sw-ready'), 2500);

  document.querySelectorAll('.poppy-swatch__img').forEach((img) => img.complete && img.naturalWidth && img.classList.add('is-loaded'));

  // ---- 1. prefetch cache wrapped around window.fetch ----
  const cache = new Map();
  const TTL = 60000;
  const nativeFetch = window.fetch.bind(window);
  const abortError = () => new DOMException('The user aborted a request.', 'AbortError');

  window.fetch = function (input, init) {
    const hit = typeof input === 'string' && cache.get(input);
    if (!hit || Date.now() - hit.at > TTL) return nativeFetch(input, init);
    const signal = init && init.signal;
    return new Promise((resolve, reject) => {
      if (signal) {
        if (signal.aborted) return reject(abortError());
        signal.addEventListener('abort', () => reject(abortError()), { once: true });
      }
      hit.promise.then((res) => resolve(res.clone()), reject);
    });
  };

  const requestUrlFor = (input) => {
    const selects = input.closest('variant-selects');
    const info = input.closest('product-info');
    if (!selects || !info) return null;
    const productUrl = input.dataset.productUrl || info.dataset.url;
    if (!productUrl || productUrl !== info.dataset.url) return null; // combined-listing swaps: leave to Dawn
    const sectionId = info.dataset.originalSection || info.dataset.section;
    const ownGroup = input.closest('fieldset');
    const ids = [];
    selects.querySelectorAll('select, fieldset').forEach((group) => {
      const chosen = group === ownGroup ? input : group.querySelector('option[selected], input:checked');
      if (chosen && chosen.dataset.optionValueId) ids.push(chosen.dataset.optionValueId);
    });
    return `${productUrl}?section_id=${sectionId}&option_values=${ids.join(',')}`;
  };

  const prefetch = (input) => {
    if (!input || input.checked || input.disabled) return;
    const url = requestUrlFor(input);
    if (!url) return;
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < TTL) return;
    if (cache.size > 40) cache.delete(cache.keys().next().value);
    const promise = nativeFetch(url).then((res) => {
      if (!res.ok) throw new Error(res.status);
      return res;
    });
    promise.catch(() => cache.delete(url));
    cache.set(url, { at: Date.now(), promise });
    if (input.dataset.poppyMediaSrc) new Image().src = input.dataset.poppyMediaSrc;
  };

  const inputFor = (el) => {
    const label = el && el.closest && el.closest('variant-selects label[for]');
    if (label) return document.getElementById(label.getAttribute('for'));
    return el && el.matches && el.matches('variant-selects input[type="radio"]') ? el : null;
  };

  let hoverTimer;
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const input = inputFor(e.target);
    if (!input) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => prefetch(input), 60);
  });
  document.addEventListener('pointerdown', (e) => prefetch(inputFor(e.target)), { passive: true });
  document.addEventListener('focusin', (e) => prefetch(inputFor(e.target)));

  // ---- 2. instant main-photo cross-fade ----
  const visibleSlide = (info) => {
    const slider = info.querySelector('media-gallery [id^="Slider-Gallery"]');
    if (!slider) return null;
    const box = slider.getBoundingClientRect();
    let best = null, bestDist = Infinity;
    slider.querySelectorAll(':scope > li').forEach((li) => {
      if (!li.offsetParent) return;
      const r = li.getBoundingClientRect();
      const dist = Math.abs(r.left - box.left);
      if (dist < bestDist) { bestDist = dist; best = li; }
    });
    return best;
  };

  const clearOverlays = (delay = 0) =>
    setTimeout(() => document.querySelectorAll('.poppy-media-overlay').forEach((o) => {
      o.classList.remove('is-in');
      setTimeout(() => o.remove(), 200);
    }), delay);

  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input.matches || !input.matches('variant-selects input[data-poppy-media-src]')) return;
    const info = input.closest('product-info');
    const slide = info && visibleSlide(info);
    const media = slide && slide.querySelector('.product__media, .media');
    const current = media && media.querySelector('img');
    if (!current) return;
    media.querySelectorAll('.poppy-media-overlay').forEach((o) => o.remove());
    const overlay = document.createElement('img');
    overlay.className = 'poppy-media-overlay';
    overlay.alt = '';
    overlay.style.objectFit = getComputedStyle(current).objectFit || 'cover';
    overlay.onload = () => requestAnimationFrame(() => overlay.classList.add('is-in'));
    overlay.src = input.dataset.poppyMediaSrc;
    media.appendChild(overlay);
    clearOverlays(5000); // safety net if the update never lands
  });

  // Dawn publishes variantChange once the gallery has been updated: fade the preview away
  const hookPubSub = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.variantChange, () => clearOverlays(250));
    return true;
  };
  if (!hookPubSub()) document.addEventListener('DOMContentLoaded', hookPubSub, { once: true });
})();
